import I18n from 'I18n';
import styled, { ThemeProvider } from 'styled-components';
import { setPrimaryColor, setTextOnPrimaryColor } from 'visitor-ui-component-library/theme/defaultThemeOperators';
import { createTheme } from 'visitor-ui-component-library/theme/createTheme';
import SVGDown from 'visitor-ui-component-library-icons/icons/SVGDown';
import SVGEllipses from 'visitor-ui-component-library-icons/icons/SVGEllipses';
import VizExIconButton from 'visitor-ui-component-library/button/VizExIconButton';
import { WHITE, NEUTRAL_1600 } from 'visitor-ui-component-library/constants/WidgetColors';
import { getFocusRingStyles } from 'visitor-ui-component-library/utils/getFocusRingStyles';
import { getIsResponderAI } from 'conversations-internal-schema/responders/operators/responderGetters';
import ChatHeadingAvatars from '../widget-header/ChatHeadingAvatars';
import { getWidgetTitleText } from '../operators/getWidgetTitleText';
import { useChatWidgetLocale } from '../ChatWidgetLocaleContext';
import ChatMenu from '../../presentation-components/ChatMenu';
import { THREAD_VIEW } from '../constants/views';
import { HEADER_TEXT_TITLE_ID } from '../constants/textIds';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SpotlightHeaderWrapper = styled.header.withConfig({
  displayName: "SpotlightWidgetHeader__SpotlightHeaderWrapper"
})(["background-color:", ";border-radius:16px 16px 0 0;padding:12px 16px;box-sizing:border-box;box-shadow:0px 1px 5px 0px rgba(0,0,0,0.05);position:relative;z-index:1;"], WHITE);
const SpotlightHeaderContent = styled.div.withConfig({
  displayName: "SpotlightWidgetHeader__SpotlightHeaderContent"
})(["display:flex;align-items:center;justify-content:space-between;width:100%;height:34px;"]);
const HeaderActions = styled.div.withConfig({
  displayName: "SpotlightWidgetHeader__HeaderActions"
})(["display:flex;align-items:center;gap:8px;"]);
const AgentPillButton = styled.button.withConfig({
  displayName: "SpotlightWidgetHeader__AgentPillButton"
})(["display:flex;align-items:center;gap:8px;background:transparent;border:none;border-radius:9999px;padding:0 8px 0 0;cursor:pointer;text-align:left;transition:background-color 150ms ease-out;&:hover{background-color:rgba(0,0,0,0.06);}", ""], getFocusRingStyles({
  ringColor: NEUTRAL_1600
}));
const TextColumn = styled.div.withConfig({
  displayName: "SpotlightWidgetHeader__TextColumn"
})(["display:flex;flex-direction:column;justify-content:center;"]);
const NameRow = styled.div.withConfig({
  displayName: "SpotlightWidgetHeader__NameRow"
})(["display:flex;align-items:center;gap:10px;"]);
const NameText = styled.p.withConfig({
  displayName: "SpotlightWidgetHeader__NameText"
})(["font-size:14px;font-weight:400;line-height:20px;color:", ";margin:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"], NEUTRAL_1600);
const SubtitleText = styled.p.withConfig({
  displayName: "SpotlightWidgetHeader__SubtitleText"
})(["font-size:12px;font-weight:300;line-height:14px;letter-spacing:0;color:#666;margin:0;white-space:nowrap;"]);
const SpotlightWidgetHeader = ({
  availabilityMessage,
  chatHeadingConfig,
  chatHeadingResponders,
  coloring,
  isThreadClosed = false,
  isThreadPersisted = false,
  isUngatedForCloseThread = false,
  onAgentClick,
  onClose = () => {},
  onEndChat,
  view = THREAD_VIEW
}) => {
  const locale = useChatWidgetLocale();
  const titleText = getWidgetTitleText(chatHeadingConfig, chatHeadingResponders, locale) || I18n.text('conversations-visitor-experience-components.default.agent');
  const isResponderAI = chatHeadingResponders.some(responder => getIsResponderAI(responder) === true);
  const subtitle = isResponderAI ? I18n.text('conversations-visitor-experience-components.isResponderAI') : availabilityMessage;
  const menuItems = [];
  if (isUngatedForCloseThread && view === THREAD_VIEW) {
    menuItems.push({
      id: 'end-chat',
      label: I18n.text('conversations-visitor-experience-components.chatMenu.endChat'),
      onClick: onEndChat !== null && onEndChat !== void 0 ? onEndChat : () => {},
      testId: 'end-chat-menu-item',
      disabled: !isThreadPersisted || isThreadClosed
    });
  }
  return /*#__PURE__*/_jsx(ThemeProvider, {
    theme: createTheme(setPrimaryColor(coloring.accentColor), setTextOnPrimaryColor(NEUTRAL_1600)),
    children: /*#__PURE__*/_jsx(SpotlightHeaderWrapper, {
      role: "banner",
      "aria-labelledby": HEADER_TEXT_TITLE_ID,
      "data-test-id": "spotlight-widget-header",
      children: /*#__PURE__*/_jsxs(SpotlightHeaderContent, {
        children: [/*#__PURE__*/_jsxs(AgentPillButton, {
          onClick: onAgentClick,
          "aria-label": I18n.text('conversations-visitor-experience-components.visitorExperienceAriaLabels.spotlightViewHistory'),
          children: [/*#__PURE__*/_jsx(ChatHeadingAvatars, {
            chatHeadingConfig: chatHeadingConfig,
            chatHeadingResponders: chatHeadingResponders,
            showStatusIndicator: true,
            borderColor: WHITE,
            size: "ms"
          }), /*#__PURE__*/_jsxs(TextColumn, {
            children: [/*#__PURE__*/_jsxs(NameRow, {
              children: [/*#__PURE__*/_jsx(NameText, {
                id: HEADER_TEXT_TITLE_ID,
                "data-test-id": "spotlight-header-name",
                children: titleText
              }), /*#__PURE__*/_jsx(SVGDown, {
                width: 12,
                height: 12,
                "aria-hidden": "true"
              })]
            }), subtitle && /*#__PURE__*/_jsx(SubtitleText, {
              "data-test-id": "spotlight-header-subtitle",
              children: subtitle
            })]
          })]
        }), /*#__PURE__*/_jsxs(HeaderActions, {
          children: [menuItems.length > 0 && /*#__PURE__*/_jsx(ChatMenu, {
            items: menuItems,
            focusRingColor: NEUTRAL_1600,
            menuTriggerAriaLabel: I18n.text('conversations-visitor-experience-components.chatMenu.menuTrigger'),
            menuAriaLabel: I18n.text('conversations-visitor-experience-components.chatMenu.menuLabel'),
            menuTriggerIcon: SVGEllipses,
            triggerSize: "ms"
          }), /*#__PURE__*/_jsx(VizExIconButton, {
            onClick: onClose,
            "aria-label": I18n.text('conversations-visitor-experience-components.visitorExperienceAriaLabels.close'),
            "data-test-id": "spotlight-minimize-button",
            use: "transparent-on-primary",
            size: "ms",
            children: /*#__PURE__*/_jsx(SVGDown, {
              width: 16,
              height: 16,
              "aria-hidden": "true"
            })
          })]
        })]
      })
    })
  });
};
SpotlightWidgetHeader.displayName = 'SpotlightWidgetHeader';
export default SpotlightWidgetHeader;